import { Button, Modal, Space } from 'antd';
import { IModal } from 'common/models/model.interface';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import QRCodeAdmitGuestModal from './eiq-secure-qr-code-admit-guest-modal';
import QRCodeAdmitResidentModal from './eiq-secure-qr-code-admit-resident-modal';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { observer } from 'mobx-react-lite';
import { IQRCodeAdmitGuestModalProps, IQRCodeAdmitResidentModalProps } from './eiq-secure-qr-code-admit-modal.types';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { EntryType } from 'common/enums/entry-type.enum';
import guestService from 'common/services/guest-service/guest.service';
import { notification } from 'common/utils/notification';
import { IAdmitResident } from 'common/services/guest-service/guest.service.types';
import './eiq-secure-qr-code-scanner.scss';
import QRCodeScanDetector from '../eiq-secure-qr-code-scan-detector/eiq-secure-qr-code-scan-detector';
import { getCode } from 'common/helpers/eiq-passport.helper';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { IFastAccessPassRead } from 'common/services/fast-access-service/fast-access-service.types';
import featureStore from 'common/stores/features/feature.store';
import { IGuestRead } from 'common/services/residents-service/residents.service.types';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import successSound from 'assets/sounds/success.wav';
import errorSound from 'assets/sounds/error.wav';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import TurnbackModal, { ITurnbackModalProps } from '../turnback-modal/turnback-modal';
import { Admit, TurnBack } from 'features/eiq-secure/constans/admit-actions.constants';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import AdvisoryAlertModal, { IAdvisoryAlertModalProps } from '../advisory-alert-modal/advisory-alert-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import { getNotesFromCheckResult } from 'features/eiq-secure/heplers/admit-notes.helper';

const QRcodeReaderId = 'eiq-fast-access-reader-id';

const openModal = (
  modal: ReturnType<typeof useModal>,
  initData: IAdvisoryAlertModalProps | IQRCodeAdmitGuestModalProps | IQRCodeAdmitResidentModalProps | ITurnbackModalProps,
) => {
  modal.setModalInitData(initData);
  modal.showModal();
};

const playSound = (sound: string) => {
  new Audio(sound).play();
};

const notifySuccess = (message: string) => {
  notification.success({ message });
  plateRecognitionContext.clear();
  playSound(successSound);
};

const handleScanError = (error: any) => {
  playSound(errorSound);
  if (error.response?.status === 404) {
    notification.error({ message: error.response.data.message, duration: 0 });
  }
};

const hasAlert = ({ residentAlertMessage, addressAlertMessage }: IFastAccessPassRead) =>
  featureStore.isFastAccessAlertsEnabled && (residentAlertMessage || addressAlertMessage);

const hasRestriction = (guest: IGuestRead | null) => featureStore.isFastAccessRestrictionsEnabled && guest?.restrictions;

const QRCodeScannerModal: FC<IModal<any, any>> = observer(({ isOpen, onClose }) => {
  const admitGuestModal = useModal();
  const admitResidentModal = useModal();
  const scanner = useRef<Html5Qrcode | null>(null);
  const pendingPassport = useRef<IFastAccessPassRead | null>(null);
  const [admitGuestData, setAdmitGuestData] = useState<any>();

  const clearGuestAdmission = () => {
    pendingPassport.current = null;
    setAdmitGuestData(undefined);
  };

  const saveGuestAction = async (action: typeof Admit | typeof TurnBack, reason?: string) => {
    if (!admitGuestData) {
      return;
    }

    const { residentId, addressId, guestId } = admitGuestData;
    const isTurnBack = action === TurnBack;

    await (isTurnBack
      ? guestService.turnbackGuest(residentId, addressId, guestId, { ...admitGuestData, reason })
      : guestService.admitGuest(residentId, addressId, guestId, admitGuestData));

    notifySuccess(isTurnBack ? 'The guest has been turned back' : 'Guest has been admitted');
    clearGuestAdmission();
  };

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, turnbackReason) => {
      if (!isSuccessSaved) {
        clearGuestAdmission();
        return;
      }
      trackPromise(saveGuestAction(TurnBack, turnbackReason));
    },
  });

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (!isSuccess) {
        clearGuestAdmission();
        return;
      }
      if (type == Admit) {
        trackPromise(approveDrivingPrivileges());
      }
      if (type == TurnBack) {
        openModal(turnbackModal, { initReason: 'Driving privileges revoked' });
      }
    },
  });

  const visitorBanAlertModal = useModal({
    onClose: (isSuccess, type) => {
      if (!isSuccess) {
        clearGuestAdmission();
        return;
      }
      if (type === Admit) {
        saveGuestAction(Admit);
      }
      if (type === TurnBack) {
        openModal(turnbackModal, { initReason: 'On banned list' });
      }
    },
  });

  const handleOnClose = () => {
    if (scanner.current) {
      if (scanner.current.isScanning) {
        scanner.current.stop().then(() => scanner.current!.clear());
      } else {
        scanner.current.clear();
      }
    }
    onClose();
  };

  const showAdmissionModal = (passport: IFastAccessPassRead) => {
    const { guest, carExpirationDate, deviceId } = passport;
    const commonData = {
      addressId: passport.addressId,
      address: passport.address,
      residentId: passport.residentId,
      resident: passport.resident,
      gate: userAuthStore.defaultGate,
      carLicensePlate: passport.carNumber,
      carLicenseState: passport.carState,
      isEIQPassportUsed: true,
      residentAlertMessage: passport.residentAlertMessage,
      addressAlertMessage: passport.addressAlertMessage,
    };

    const initData: IQRCodeAdmitGuestModalProps | IQRCodeAdmitResidentModalProps = guest
      ? { ...commonData, guest }
      : { ...commonData, carExpirationDate, deviceId };

    openModal(guest ? admitGuestModal : admitResidentModal, initData);
  };

  const showAdvisoryWarning = async (passport: IFastAccessPassRead, guestData: any) => {
    const { guest, carNumber } = passport;
    if (!guest || !featureStore.isEIQAdvisoryEnabled) {
      return false;
    }

    const result = await trackPromise(
      visitorBanListService.check({
        licensePlate: carNumber,
        firstName: guest.firstName,
        lastName: guest.lastName,
        companyName: guest.companyName,
      }),
    );

    if (!result.hasMatch && !result.hasPartialMatch) {
      return false;
    }

    const note = getNotesFromCheckResult(result);

    setAdmitGuestData({ ...guestData, isAdvisoryAdmit: true, note: note });
    const modalInitData: IAdvisoryAlertModalProps = {
      data: result,
      type: 'BanList',
      isVacantAddress: false,
      isShowReason: !!configurationStore.configuration?.advisory.isDisplayReasonForSecureOfficer,
    };

    openModal(visitorBanAlertModal, modalInitData);
    return true;
  };

  const approveDrivingPrivileges = async () => {
    const passport = pendingPassport.current;
    if (!passport || !admitGuestData) {
      return;
    }

    try {
      if (await showAdvisoryWarning(passport, admitGuestData)) {
        return;
      }
      await saveGuestAction(Admit);
    } catch (error: any) {
      clearGuestAdmission();
      handleScanError(error);
    }
  };

  const showGuestWarning = async (passport: IFastAccessPassRead, admitData: IAdmitResident) => {
    const { guest, residentId, addressId, carNumber } = passport;
    if (!guest) {
      return false;
    }

    const guestData = { ...admitData, residentId, addressId, guestId: guest.id };
    if (featureStore.isEIQIntelliTicketEnabled) {
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(carNumber!));

      if (result.isDrivingPrivilegesRevoked) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'DrivingPrivileges',
          isVacantAddress: false,
          isShowReason: true,
        };

        pendingPassport.current = passport;
        setAdmitGuestData({ ...guestData, isDrivingPrivilagesRevoked: true });
        openModal(drivingPrivilagesRevokeModal, modalInitData);
        return true;
      }
    }

    return showAdvisoryWarning(passport, guestData);
  };

  const getDecodedTextHandle = async (text: string) => {
    try {
      const passport: IFastAccessPassRead | null = await fastAccessService.getFastAccessPass(text);
      if (!passport) {
        notification.error({ message: 'Some error occurred' });
        return;
      }

      const { addressId, residentId, guest, carExpirationDate, carNumber, carState, deviceId } = passport;
      const gate = userAuthStore.defaultGate;
      const shouldShowGuestModal = guest && (featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled);
      const shouldShowAdmissionModal =
        !carNumber ||
        !carState ||
        !gate ||
        isServiceGuest(guest?.type) ||
        hasAlert(passport) ||
        hasRestriction(guest) ||
        shouldShowGuestModal;

      if (shouldShowAdmissionModal) {
        showAdmissionModal(passport);
        return;
      }

      const admitData: IAdmitResident = {
        gate,
        entryType: EntryType.Driver,
        carState,
        carLicensePlate: carNumber,
        carExpirationDate,
        deviceId,
        isEIQPassportUsed: true,
      };

      if (guest && (await showGuestWarning(passport, admitData))) {
        return;
      }

      if (guest) {
        await guestService.admitGuest(residentId, addressId, guest.id, admitData);
      } else {
        await guestService.admitResident(residentId, addressId, admitData);
      }
      notifySuccess(guest ? 'Guest has been admitted' : 'The resident  has been admitted');
    } catch (error: any) {
      handleScanError(error);
    }
  };

  const getScanner = (): Html5Qrcode => {
    if (!scanner.current) {
      scanner.current = new Html5Qrcode(QRcodeReaderId);
    }
    return scanner.current;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    getScanner()
      .start(
        { facingMode: 'environment' },
        { fps: 5, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          if (decodedText) {
            getDecodedTextHandle(getCode(decodedText));
          }
          handleOnClose();
        },
        (error) => console.log(error),
      )
      .catch((error) => console.log(error));
  }, [isOpen]);

  const onScanDetected = (code: string) => {
    getDecodedTextHandle(code.trim());
  };

  return (
    <>
      <Modal centered width={400} open={isOpen} wrapClassName="custom-modal custom-modal--so" closable={false} title="EIQ Passport scanner">
        <div className="qr-code-scanner" id={QRcodeReaderId} />
        <Space className="footer">
          <Button className="eiq-button" type="link" onClick={handleOnClose}>
            Cancel
          </Button>
        </Space>
      </Modal>
      {admitGuestModal.isOpenModal && (
        <QRCodeAdmitGuestModal
          isOpen={admitGuestModal.isOpenModal}
          onClose={admitGuestModal.hideModal}
          initData={admitGuestModal.initData}
        />
      )}
      {admitResidentModal.isOpenModal && (
        <QRCodeAdmitResidentModal
          isOpen={admitResidentModal.isOpenModal}
          onClose={admitResidentModal.hideModal}
          initData={admitResidentModal.initData}
        />
      )}
      {drivingPrivilagesRevokeModal.isOpenModal && (
        <AdvisoryAlertModal
          isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
          onClose={drivingPrivilagesRevokeModal?.hideModal}
          initData={drivingPrivilagesRevokeModal?.initData}
        />
      )}
      {visitorBanAlertModal.isOpenModal && (
        <AdvisoryAlertModal
          isOpen={visitorBanAlertModal.isOpenModal}
          onClose={visitorBanAlertModal.hideModal}
          initData={visitorBanAlertModal.initData}
        />
      )}
      {turnbackModal.isOpenModal && (
        <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      )}
      <QRCodeScanDetector onScan={onScanDetected} />
    </>
  );
});

export default React.memo(QRCodeScannerModal, (prevProps, nextProps) => prevProps.isOpen === nextProps.isOpen);
